import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, CheckCircle2, Circle, AlertCircle, ExternalLink, Sparkles } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  priority: 'critical' | 'important' | 'nice-to-have';
  link?: string;
  linkText?: string;
}

const PreLaunchChecklistPage = () => {
  const navigate = useNavigate();
  
  const [checklist, setChecklist] = useState<{ [key: string]: boolean }>({
    // Critical Items
    'leaked-password': false,
    'redirect-urls': false,
    'test-flows': false,
    'error-monitoring': false,
    'support-email': false,
    
    // Important Items
    'welcome-email': false,
    'beta-agreement': true, // Already done
    'feedback-dashboard': true, // Already done
    'known-limitations': false,
    'user-guide': false,
    
    // Nice to Have
    'rate-limiting': false,
    'video-walkthroughs': false,
    'community-channel': false,
    'analytics-dashboard': false,
    'query-optimization': false,
  });

  const criticalItems: ChecklistItem[] = [
    {
      id: 'leaked-password',
      title: 'Enable Leaked Password Protection',
      description: 'Protect users from using compromised passwords',
      priority: 'critical',
      link: 'https://supabase.com/dashboard/project/idhxhcuomiygywnwohvb/auth/providers',
      linkText: 'Supabase Auth Settings',
    },
    {
      id: 'redirect-urls',
      title: 'Configure Redirect URLs',
      description: 'Set Site URL and Redirect URLs in Supabase Auth',
      priority: 'critical',
      link: 'https://supabase.com/dashboard/project/idhxhcuomiygywnwohvb/auth/url-configuration',
      linkText: 'URL Configuration',
    },
    {
      id: 'test-flows',
      title: 'Test All User Flows',
      description: 'Complete signup, login, and core features for each account type',
      priority: 'critical',
    },
    {
      id: 'error-monitoring',
      title: 'Verify Error Monitoring',
      description: 'Check that automatic error reporting is working',
      priority: 'critical',
      link: '/admin/feedback',
      linkText: 'View Feedback Dashboard',
    },
    {
      id: 'support-email',
      title: 'Set Up Support Email',
      description: 'Create support@theslice.com and auto-responder',
      priority: 'critical',
    },
  ];

  const importantItems: ChecklistItem[] = [
    {
      id: 'welcome-email',
      title: 'Configure Welcome Emails',
      description: 'Set up email service (Resend) for welcome emails',
      priority: 'important',
    },
    {
      id: 'beta-agreement',
      title: 'Beta Agreement on Sign-Up',
      description: 'Users see beta terms during registration',
      priority: 'important',
    },
    {
      id: 'feedback-dashboard',
      title: 'Admin Feedback Dashboard',
      description: 'System to review user feedback',
      priority: 'important',
      link: '/admin/feedback',
      linkText: 'View Dashboard',
    },
    {
      id: 'known-limitations',
      title: 'Document Known Limitations',
      description: 'Create list of beta limitations and known issues',
      priority: 'important',
    },
    {
      id: 'user-guide',
      title: 'Create Basic User Guide',
      description: 'Quick start guide for each account type',
      priority: 'important',
    },
  ];

  const niceToHaveItems: ChecklistItem[] = [
    {
      id: 'rate-limiting',
      title: 'Implement Rate Limiting',
      description: 'Prevent abuse on auth and feedback endpoints',
      priority: 'nice-to-have',
    },
    {
      id: 'video-walkthroughs',
      title: 'Create Video Walkthroughs',
      description: 'Screen recordings of key features',
      priority: 'nice-to-have',
    },
    {
      id: 'community-channel',
      title: 'Set Up Community Channel',
      description: 'Discord or Slack for beta testers',
      priority: 'nice-to-have',
    },
    {
      id: 'analytics-dashboard',
      title: 'Build Analytics Dashboard',
      description: 'Track DAU, feature usage, errors',
      priority: 'nice-to-have',
    },
    {
      id: 'query-optimization',
      title: 'Optimize Database Queries',
      description: 'Review and index frequently queried columns',
      priority: 'nice-to-have',
      link: 'https://supabase.com/dashboard/project/idhxhcuomiygywnwohvb/database/query-performance',
      linkText: 'Query Performance',
    },
  ];

  const toggleItem = (id: string) => {
    setChecklist(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const allItems = [...criticalItems, ...importantItems, ...niceToHaveItems];
  const completedCount = Object.values(checklist).filter(Boolean).length;
  const totalCount = allItems.length;
  const progress = (completedCount / totalCount) * 100;

  const criticalComplete = criticalItems.every(item => checklist[item.id]);
  const importantComplete = importantItems.every(item => checklist[item.id]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'destructive';
      case 'important':
        return 'default';
      default:
        return 'secondary';
    }
  };

  const renderChecklistSection = (items: ChecklistItem[], title: string) => (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.map((item) => (
          <div
            key={item.id}
            className="flex items-start gap-3 p-3 rounded-lg border hover:bg-accent/50 transition-colors"
          >
            <Checkbox
              checked={checklist[item.id]}
              onCheckedChange={() => toggleItem(item.id)}
              className="mt-1"
            />
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-2">
                <h4 className="font-medium">{item.title}</h4>
                <Badge variant={getPriorityColor(item.priority) as any} className="text-xs">
                  {item.priority}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">{item.description}</p>
              {item.link && (
                <a
                  href={item.link}
                  target={item.link.startsWith('http') ? '_blank' : undefined}
                  rel={item.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  {item.linkText || 'View'}
                  {item.link.startsWith('http') && <ExternalLink className="h-3 w-3" />}
                </a>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-4xl">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-primary" />
            Beta Launch Checklist
          </h1>
          <p className="text-muted-foreground">
            Complete these items before sharing your beta link
          </p>
        </div>
      </div>

      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle>Progress Overview</CardTitle>
          <CardDescription>
            {completedCount} of {totalCount} items completed
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Progress value={progress} className="h-3" />
          <div className="grid gap-2 sm:grid-cols-3">
            <div className="flex items-center gap-2">
              {criticalComplete ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4 text-destructive" />
              )}
              <span className="text-sm">
                Critical: {criticalItems.filter(i => checklist[i.id]).length}/{criticalItems.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {importantComplete ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <Circle className="h-4 w-4 text-muted-foreground" />
              )}
              <span className="text-sm">
                Important: {importantItems.filter(i => checklist[i.id]).length}/{importantItems.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Circle className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                Nice-to-Have: {niceToHaveItems.filter(i => checklist[i.id]).length}/{niceToHaveItems.length}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {renderChecklistSection(criticalItems, '🔴 Critical (Must Complete)')}
        {renderChecklistSection(importantItems, '🟡 Important (Should Complete)')}
        {renderChecklistSection(niceToHaveItems, '🟢 Nice-to-Have (Can Do Post-Launch)')}
      </div>

      <Card className="border-primary/20 bg-gradient-to-r from-primary/10 to-primary/5">
        <CardHeader>
          <CardTitle>Ready to Launch?</CardTitle>
          <CardDescription>
            {criticalComplete && importantComplete
              ? '✅ You\'re ready to publish and share your beta link!'
              : criticalComplete
              ? '⚠️ All critical items complete. Consider finishing important items.'
              : '❌ Complete all critical items before launching.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <p className="text-sm">
              Once ready, click the <strong>Publish</strong> button in Lovable editor to get your public URL.
            </p>
            <Button
              className="w-full"
              disabled={!criticalComplete}
              onClick={() => window.open('https://docs.lovable.dev/user-guides/deploying', '_blank')}
            >
              View Publishing Guide
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PreLaunchChecklistPage;
